import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../Button';
import { useAuth } from '../../context/AuthContext';

const EMPTY = {
  enabled: false, eyebrow: '', tagline: '', hero_image_url: '',
  stats: [], schedule: { intro: '', groups: [], footnote: '' },
  about: { heading: '', paragraphs: [], benefits: [] },
  testing: { measurables: [] }, bring: [], important: [],
  location: { name: '', address_lines: [], maps_url: '' },
  contact: { name: '', email: '', phone: '' },
  cta: { heading: '', sub: '' },
};

// A section object where every field is blank renders as an orphan heading
// over an empty card on the public page (see task brief). The public
// renderer now guards on this too, but we still send `null` rather than
// lean on that — two independent layers, both correct.
const isBlankValue = (v) => v === '' || v === null || v === undefined
  || (Array.isArray(v) && v.length === 0);

const nullIfEmptySection = (obj) => {
  if (!obj || typeof obj !== 'object') return obj ?? null;
  return Object.values(obj).every(isBlankValue) ? null : obj;
};

// Sections that can be entirely blank and must collapse to `null` before
// PUT: about, testing, schedule, location, contact, cta. `stats`, `bring`
// and `important` are plain arrays (not section objects with a heading of
// their own), so an empty array there is already the correct "nothing to
// render" payload and needs no substitution.
const buildPayload = (page) => ({
  ...page,
  schedule: nullIfEmptySection(page.schedule),
  about: nullIfEmptySection(page.about),
  testing: nullIfEmptySection(page.testing),
  location: nullIfEmptySection(page.location),
  contact: nullIfEmptySection(page.contact),
  cta: nullIfEmptySection(page.cta),
});

/** A textarea where one line is one list item — the shape almost every list on
 *  this page has, and far less clicking than a row-per-item repeater. */
function Lines({ label, value, onChange, rows = 4, placeholder }) {
  return (
    <div className="form-field">
      <label className="form-field__label">{label}</label>
      <textarea className="form-field__input" rows={rows} placeholder={placeholder}
                value={(value || []).join('\n')}
                onChange={(e) => onChange(
                  e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                style={{ resize: 'vertical' }} />
      <span style={{ fontSize: 12, color: '#64748b' }}>One per line.</span>
    </div>
  );
}

export default function CampPageContentEditor({ campId, slug }) {
  const { API_URL } = useAuth();
  const [page, setPage] = useState(EMPTY);
  // Tracked separately from the message text on purpose -- branching on
  // status.includes('failed') would break the moment someone rewords a
  // message. setStatus and setStatusIsError are always changed together;
  // see the ok()/fail() helpers below.
  const [status, setStatus] = useState('');
  const [statusIsError, setStatusIsError] = useState(false);
  const [busy, setBusy] = useState(false);

  const ok = (text) => { setStatus(text); setStatusIsError(false); };
  const fail = (text) => { setStatus(text); setStatusIsError(true); };

  const getAxiosConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const load = useCallback(() => {
    if (!campId) return;
    axios.get(`${API_URL}/camps/${campId}/page-content`, getAxiosConfig())
      .then((r) => setPage({ ...EMPTY, ...(r.data.page_content || {}) }))
      .catch(() => fail('Could not load the page content.'));
  }, [campId, API_URL]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const setIn = (section, k, v) =>
    setPage((p) => ({ ...p, [section]: { ...(p[section] || {}), [k]: v } }));

  const save = async () => {
    setBusy(true); ok('');
    try {
      await axios.put(`${API_URL}/camps/${campId}/page-content`,
                      { page_content: buildPayload(page) }, getAxiosConfig());
      ok('Saved.');
    } catch {
      fail('Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/camps/${campId}/page-content/hero-image`,
                                 fd, getAxiosConfig());
      set('hero_image_url', r.data.hero_image_url);
      ok('Hero uploaded — remember to Save.');
    } catch {
      fail('Hero upload failed.');
    } finally {
      setBusy(false);
    }
  };

  const setGroup = (i, k, v) => setIn('schedule', 'groups',
    (page.schedule?.groups || []).map((g, j) => (j === i ? { ...g, [k]: v } : g)));

  const setStat = (i, k, v) => set('stats',
    (page.stats || []).map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  if (!campId) {
    return <p style={{ fontSize: 13, color: '#64748b' }}>
      Save the camp first, then build its description page.
    </p>;
  }

  return (
    <div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input type="checkbox" checked={!!page.enabled}
               onChange={(e) => set('enabled', e.target.checked)} />
        Publish the description page at /camp/{slug}
      </label>

      <div className="form-row">
        <div className="form-field">
          <label className="form-field__label">Eyebrow</label>
          <input className="form-field__input" value={page.eyebrow || ''}
                 placeholder="UNDERCLASSMAN SHOWCASE · CLASSES 2028–2031"
                 onChange={(e) => set('eyebrow', e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-field__label">Tagline</label>
          <input className="form-field__input" value={page.tagline || ''}
                 placeholder="COMPETE. GET VERIFIED. GET RECRUITED."
                 onChange={(e) => set('tagline', e.target.value)} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-field__label" htmlFor="cpc-hero">Hero image</label>
          <input id="cpc-hero" type="file" accept="image/png,image/jpeg,image/webp"
                 onChange={(e) => uploadHero(e.target.files?.[0])} />
          {page.hero_image_url && (
            <img src={page.hero_image_url} alt="" style={{ maxHeight: 120, marginTop: 8 }} />
          )}
        </div>
      </div>

      <div className="form-field">
        <label className="form-field__label">Stat band</label>
        {(page.stats || []).map((s, i) => (
          <div key={i} className="form-row" style={{ gap: 8 }}>
            <input className="form-field__input" placeholder="30+" value={s.value || ''}
                   onChange={(e) => setStat(i, 'value', e.target.value)} />
            <input className="form-field__input" placeholder="Colleges in attendance"
                   value={s.caption || ''}
                   onChange={(e) => setStat(i, 'caption', e.target.value)} />
            <Button variant="secondary" size="sm"
                    onClick={() => set('stats', page.stats.filter((_, j) => j !== i))}>
              ✕
            </Button>
          </div>
        ))}
        <Button variant="secondary" size="sm"
                onClick={() => set('stats', [...(page.stats || []), { value: '', caption: '' }])}>
          + Add stat
        </Button>
      </div>

      <div className="form-field">
        <label className="form-field__label">Schedule intro</label>
        <textarea className="form-field__input" rows={2} value={page.schedule?.intro || ''}
                  onChange={(e) => setIn('schedule', 'intro', e.target.value)} />
      </div>
      <div className="form-field">
        <label className="form-field__label">Schedule groups</label>
        {(page.schedule?.groups || []).map((g, i) => (
          <div key={i} className="form-row" style={{ gap: 8 }}>
            <input className="form-field__input" placeholder="GROUP 1" value={g.label || ''}
                   onChange={(e) => setGroup(i, 'label', e.target.value)} />
            <input className="form-field__input" placeholder="OL / DL" value={g.title || ''}
                   onChange={(e) => setGroup(i, 'title', e.target.value)} />
            <input className="form-field__input" placeholder="8:00 AM" value={g.checkin || ''}
                   onChange={(e) => setGroup(i, 'checkin', e.target.value)} />
            <input className="form-field__input" placeholder="9:00 – 11:00 AM"
                   value={g.field_time || ''}
                   onChange={(e) => setGroup(i, 'field_time', e.target.value)} />
            <Button variant="secondary" size="sm"
                    onClick={() => setIn('schedule', 'groups',
                      page.schedule.groups.filter((_, j) => j !== i))}>✕</Button>
          </div>
        ))}
        <Button variant="secondary" size="sm"
                onClick={() => setIn('schedule', 'groups',
                  [...(page.schedule?.groups || []),
                   { label: '', title: '', checkin: '', field_time: '' }])}>
          + Add group
        </Button>
      </div>
      <div className="form-field">
        <label className="form-field__label">Schedule footnote</label>
        <textarea className="form-field__input" rows={2} value={page.schedule?.footnote || ''}
                  onChange={(e) => setIn('schedule', 'footnote', e.target.value)} />
      </div>

      <div className="form-field">
        <label className="form-field__label">About heading</label>
        <input className="form-field__input" value={page.about?.heading || ''}
               onChange={(e) => setIn('about', 'heading', e.target.value)} />
      </div>
      <Lines label="About paragraphs" rows={5} value={page.about?.paragraphs}
             onChange={(v) => setIn('about', 'paragraphs', v)} />
      <Lines label="Why this showcase matters (green checks)" rows={6}
             value={page.about?.benefits}
             onChange={(v) => setIn('about', 'benefits', v)} />
      <Lines label="Verified testing pills" rows={2} value={page.testing?.measurables}
             placeholder={'HEIGHT\nWEIGHT\nWING SPAN\nHAND SIZE'}
             onChange={(v) => setIn('testing', 'measurables', v)} />
      <Lines label="What to bring" value={page.bring} onChange={(v) => set('bring', v)} />
      <Lines label="Important information" rows={5} value={page.important}
             onChange={(v) => set('important', v)} />

      <div className="form-row">
        <div className="form-field">
          <label className="form-field__label">Venue name</label>
          <input className="form-field__input" value={page.location?.name || ''}
                 onChange={(e) => setIn('location', 'name', e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-field__label">Google Maps URL</label>
          <input className="form-field__input" value={page.location?.maps_url || ''}
                 onChange={(e) => setIn('location', 'maps_url', e.target.value)} />
        </div>
      </div>
      <Lines label="Address" rows={2} value={page.location?.address_lines}
             placeholder={'4750 Vernon Ave\nLa Grange, IL 60525'}
             onChange={(v) => setIn('location', 'address_lines', v)} />

      <div className="form-row">
        <div className="form-field">
          <label className="form-field__label">Contact name</label>
          <input className="form-field__input" value={page.contact?.name || ''}
                 onChange={(e) => setIn('contact', 'name', e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-field__label">Contact email</label>
          <input className="form-field__input" value={page.contact?.email || ''}
                 onChange={(e) => setIn('contact', 'email', e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-field__label">Contact phone</label>
          <input className="form-field__input" value={page.contact?.phone || ''}
                 onChange={(e) => setIn('contact', 'phone', e.target.value)} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label className="form-field__label">Closing CTA heading</label>
          <input className="form-field__input" value={page.cta?.heading || ''}
                 onChange={(e) => setIn('cta', 'heading', e.target.value)} />
        </div>
        <div className="form-field">
          <label className="form-field__label">Closing CTA subtitle</label>
          <input className="form-field__input" value={page.cta?.sub || ''}
                 onChange={(e) => setIn('cta', 'sub', e.target.value)} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
        <Button variant="primary" disabled={busy} onClick={save}>Save description page</Button>
        {slug && (
          <a href={`/camp/${slug}`} target="_blank" rel="noreferrer"
             style={{ fontSize: 13 }}>View page →</a>
        )}
        {status && (statusIsError
          ? <div className="form-field__error" role="alert">{status}</div>
          : <span style={{ fontSize: 13, color: '#64748b' }}>{status}</span>)}
      </div>
    </div>
  );
}

// Exported for the unit tests below — pure and side-effect free, so testing
// it directly is a stronger, cheaper oracle than driving it through the DOM.
export { buildPayload, nullIfEmptySection };
